import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {EnhancedWorkpace} from '@/types/Workspace'
import Domain from '@/types/Domain'
import {
    NameResolver,
    PropertySignature,
    inverseMultiplicityOf,
    multiplicityOf,
    propertySignature
} from '@/utils/propertyDefinition'
import {workspaceNames} from '@/utils/workspaceNames'
import {METRICS} from '@/utils/classDiagram'

/**
 * O que uma caixa de classe mostra.
 *
 * É a ponte entre o workspace e a notação: o propertyDefinition sabe descrever UMA
 * propriedade e não conhece o workspace; o classDiagram sabe posicionar caixas e não
 * conhece o domínio. Este módulo é o único que conhece os dois.
 */

/** O outro lado de uma ligação. Só as linhas de relacionamento têm. */
export type ClassTarget = {
    alias: string
    name: string
    /** Quantos itens desta coleção pelo mesmo item do destino */
    inverseMultiplicity: string
    /** Espelho de um relacionamento declarado do outro lado */
    mirror: boolean
    /**
     * A propriedade do OUTRO lado que descreve esta mesma ligação, quando existe: o
     * espelho, visto do lado direto; o dono, visto do espelho. É ela que a linha procura
     * ao chegar na outra caixa.
     */
    counterpart: string | null
    /**
     * Navegável dos dois lados: existe propriedade nas duas coleções.
     *
     * Não é o mesmo que `mirror`. O espelho é sempre bidirecional — ele É o outro lado.
     * Mas o lado direto só é bidirecional quando alguém o espelhou; sem isso a ligação
     * existe só de cá para lá, e a seta única diz a verdade.
     */
    bidirectional: boolean
}

/**
 * O domínio visto de uma linha de seleção. Domínio excluído com o atributo ainda
 * apontando para ele cai no alias como nome — a linha continua legível, e o diagrama não
 * inventa uma caixa para o que não existe mais.
 */
export type ClassDomainRef = { alias: string; name: string }

/**
 * Uma linha de compartimento.
 *
 * Atributo e relacionamento são o MESMO tipo de linha porque os compartimentos deixaram
 * de separá-los: o que separa agora é fazer ou não parte do identificador, e isso corta
 * os dois. Quem é relacionamento leva um `target` junto.
 */
export type ClassRow = PropertySignature & {
    target: ClassTarget | null
    /**
     * O domínio, quando a propriedade é do tipo seleção.
     *
     * É o outro lado de uma ligação, como o `target` do relacionamento — só que o
     * destino é a caixa de um domínio, e não de uma coleção. Leva alias E nome pelo mesmo
     * motivo que o `target`: o alias acha a caixa, e o nome é o que se escreve.
     */
    domain: ClassDomainRef | null
}

export type ClassState = {
    id: number
    name: string
    color: string
    initial: boolean
    final: boolean
}

export type ClassWorkflow = {
    name: string
    states: ClassState[]
}

export type ClassModel = {
    alias: string
    name: string
    /**
     * 1º compartimento: o identificador, atributo ou relacionamento, na ordem em que o
     * compõe — e a ordem importa, porque é nela que o backend concatena as partes da
     * chave ao montar o identificador de uma instância.
     */
    identifiers: ClassRow[]
    /** 2º compartimento: o que não faz parte da chave */
    others: ClassRow[]
    /** 3º compartimento: null quando não há fluxo — ausência informa, não vira caixa vazia */
    workflow: ClassWorkflow | null
}

/**
 * O que se clicou numa caixa: o cabeçalho, uma propriedade, ou o fluxo.
 *
 * Mora aqui porque nomeia as partes do próprio modelo de classe, e porque os três que
 * conversam por ele — a caixa, o diagrama e a tela — já importam deste módulo.
 */
export type BoxTarget =
    | { kind: 'class' }
    | { kind: 'workflow' }
    | { kind: 'property'; property: string }

/**
 * O mesmo alvo, já dizendo de qual coleção veio. É o que a tela recebe.
 *
 * Escrito por extenso, e não como `ClassTarget & {idea}`: a interseção de uma união com
 * um objeto faz o TypeScript recusar literais que são válidos em qualquer um dos ramos.
 */
export type DiagramTarget =
    | { kind: 'class'; idea: string }
    | { kind: 'workflow'; idea: string }
    | { kind: 'property'; property: string; idea: string }
    /**
     * A caixa de um domínio. É o único alvo que não vem de uma coleção — o domínio é
     * entidade do ambiente, e a caixa dele não pertence a caixa nenhuma.
     */
    | { kind: 'domain'; domain: string }

/** As linhas na ordem em que a caixa as desenha. */
export function rowsOf(model: ClassModel): ClassRow[] {
    return [...model.identifiers, ...model.others]
}

export function rowOf(model: ClassModel, alias: string): ClassRow | undefined {
    return rowsOf(model).find(row => row.alias === alias)
}

/**
 * O alias da propriedade que espelha esta de volta, na coleção de destino.
 *
 * Procura lá uma propriedade cujo espelho aponte para esta propriedade E para esta
 * coleção. As duas condições importam: só o alias da propriedade casaria com o de
 * outra coleção qualquer que tivesse um relacionamento de mesmo nome.
 */
function mirroredBy(idea: Idea, property: IdeaProperty, workspace: EnhancedWorkpace): string | null {
    const target = workspace.ideaWith(property.relationship!.idea)
    if (!target) return null
    const found = target.properties.find(other => {
        const relationship = other.relationship
        return !!relationship && relationship.reverse === property.alias && relationship.idea === idea.alias
    })
    return found ? found.alias : null
}

function target(
    idea: Idea, property: IdeaProperty, workspace: EnhancedWorkpace, names: NameResolver
): ClassTarget | null {
    const relationship = property.relationship
    if (!relationship) return null
    //No espelho o outro lado é o dono, e o alias dele já está gravado aqui
    const counterpart = relationship.reverse || mirroredBy(idea, property, workspace)
    return {
        alias: relationship.idea,
        name: names.idea(relationship.idea),
        inverseMultiplicity: inverseMultiplicityOf(property, names),
        mirror: !!relationship.reverse,
        counterpart: counterpart || null,
        bidirectional: !!counterpart
    }
}

function domainRef(property: IdeaProperty, workspace: EnhancedWorkpace): ClassDomainRef | null {
    if (!property.domain) return null
    const domain = workspace.domainWith(property.domain)
    return {alias: property.domain, name: domain ? domain.name : property.domain}
}

function classRow(
    idea: Idea, property: IdeaProperty, workspace: EnhancedWorkpace, names: NameResolver
): ClassRow {
    const signature = propertySignature(property, names)
    const domain = domainRef(property, workspace)
    return {
        ...signature,
        //O tipo de uma seleção é o DOMÍNIO, e não a palavra "Seleção": é o mesmo que a
        //linha de relacionamento faz ao escrever a coleção de destino no lugar de
        //"Relacionamento". Que o tipo é seleção está na marca e na seta até a caixa dele.
        typeLabel: domain ? domain.name : signature.typeLabel,
        target: target(idea, property, workspace, names),
        domain
    }
}

/**
 * O fluxo da coleção, resolvido em memória pelo workflowOf — o alias já veio na mesma
 * resposta que trouxe as coleções, então não há ida ao servidor aqui.
 *
 * A cor sai do coloredStates do próprio fluxo: a situação pode ter cor escolhida, e quando
 * não tem, a derivada depende da ORDEM — reordenar troca a cor de todas as automáticas.
 */
function classWorkflow(idea: Idea, workspace: EnhancedWorkpace): ClassWorkflow | null {
    const workflow = workspace.workflowOf(idea)
    if (!workflow) return null
    return {
        name: workflow.name,
        states: workflow.coloredStates().map(state => ({
            id: state.id,
            name: state.name,
            color: state.color,
            initial: !!state.initial,
            final: !!state.final
        }))
    }
}

export function classModel(idea: Idea, workspace: EnhancedWorkpace, names: NameResolver): ClassModel {
    const rows = (idea.properties || []).map(p => classRow(idea, p, workspace, names))
    return {
        alias: idea.alias,
        name: idea.name,
        //A ordem de declaração se preserva dentro de cada compartimento: no identificador
        //ela é o que define como as partes da chave se concatenam
        identifiers: rows.filter(row => row.identifier),
        others: rows.filter(row => !row.identifier),
        workflow: classWorkflow(idea, workspace)
    }
}

export function classModels(workspace: EnhancedWorkpace): ClassModel[] {
    const names = workspaceNames(workspace)
    return workspace.ideas.map(idea => classModel(idea, workspace, names))
}

/**
 * Altura da caixa, em pixels, sem medir o DOM.
 *
 * Só fecha porque a largura é fixa e toda linha é de uma linha só, com reticências:
 * aí a altura é a soma dos compartimentos que existem. Compartimento vazio não entra —
 * a coleção sem chave declarada não ganha um primeiro compartimento vazio.
 */
function compartmentHeight(rows: number, title = 0): number {
    const {row, section, padding, divider} = METRICS
    return rows || title ? divider + padding * 2 + title * section + rows * row : 0
}

export function boxHeight(model: ClassModel): number {
    return METRICS.border * 2 + METRICS.header +
        compartmentHeight(model.identifiers.length) +
        compartmentHeight(model.others.length) +
        compartmentHeight(model.workflow ? model.workflow.states.length : 0, model.workflow ? 1 : 0)
}

/**
 * A caixa do domínio: cabeçalho e UM compartimento, o das opções.
 *
 * Sem faixa de título nesse compartimento — o nome do domínio já é o cabeçalho da caixa.
 * É o que a diferencia do compartimento de fluxo, que precisa da faixa porque ali o
 * cabeçalho é o da coleção.
 */
export function domainHeight(domain: Domain): number {
    return METRICS.border * 2 + METRICS.header + compartmentHeight(domain.options.length)
}

/**
 * Onde a ligação encosta na caixa, medido do topo dela.
 *
 * Sai da MESMA aritmética do boxHeight — se uma das duas mudar sozinha, a linha aponta
 * para a linha errada. Por isso as duas leem as mesmas métricas e contam os
 * compartimentos na mesma ordem.
 */

/** Centro do cabeçalho: é onde chega a ligação que o outro lado não espelha. */
export function headerAnchor(): number {
    return METRICS.border + METRICS.header / 2
}

/** Centro da linha de uma propriedade, ou undefined se ela não está nesta caixa. */
export function rowAnchor(model: ClassModel, alias: string): number | undefined {
    const {border, header, row, padding, divider} = METRICS
    const top = border + header

    const key = model.identifiers.findIndex(entry => entry.alias === alias)
    if (key >= 0) return top + divider + padding + key * row + row / 2

    const other = model.others.findIndex(entry => entry.alias === alias)
    if (other < 0) return undefined
    return top + compartmentHeight(model.identifiers.length) +
        divider + padding + other * row + row / 2
}

export type ClassEdge = {
    from: string
    to: string
    /**
     * Alias da propriedade que origina a ligação, na coleção `from`.
     *
     * É o que permite clicar na linha e cair na propriedade certa. O `label` não serve:
     * ele é o nome, que não identifica nada.
     */
    alias: string
    /** Junto à origem */
    sourceLabel: string
    /** Junto ao destino */
    targetLabel: string
    label: string
    /** Navegável dos dois lados: a linha leva ponta nas duas extremidades */
    bidirectional: boolean
    /**
     * Qual das pontas é a dona, quando a ligação compõe o identificador da outra. É ali
     * que vai o diamante. `null` quando nenhuma identidade depende da outra.
     */
    owner: 'source' | 'target' | null
    /** Onde encostar na caixa de origem: a linha da própria propriedade */
    sourceAnchor?: number
    /** Onde encostar na de destino: a linha do espelho, ou o cabeçalho se não houver */
    targetAnchor?: number
}

/**
 * Quem é o dono da ligação.
 *
 * Um relacionamento que faz parte do identificador diz que este item não se identifica
 * sem aquele: a identidade de cá depende da de lá, e quem manda é o OUTRO lado. Em UML
 * isso é composição, e o diamante fica na ponta do dono.
 *
 * Olha as duas pontas porque a dependência pode estar declarada de qualquer lado — em
 * geral está no relacionamento direto, mas um espelho também pode compor a chave.
 */
function ownerOf(row: ClassRow, counterpart: ClassRow | undefined): 'source' | 'target' | null {
    if (row.identifier) return 'target'
    if (counterpart && counterpart.identifier) return 'source'
    return null
}

/**
 * As arestas do diagrama.
 *
 * Só relacionamentos DIRETOS viram linha: o espelho descreve a mesma associação vista
 * do outro lado, e desenhá-lo daria duas linhas para uma ligação só. Ele continua
 * aparecendo como linha no compartimento da caixa, que é onde informa.
 *
 * Aresta para coleção que não existe mais é descartada — um relacionamento pode
 * sobreviver à exclusão do destino, e uma aresta órfã quebraria o layout.
 *
 * A linha encosta na PROPRIEDADE, não num ponto qualquer da borda: sai da linha do
 * relacionamento na caixa de origem e chega na linha do espelho, na de destino. Sem
 * espelho não há linha correspondente do outro lado, e aí ela chega no cabeçalho — a
 * ligação é com a coleção inteira, não com um campo dela.
 */
export function classEdges(models: ClassModel[]): ClassEdge[] {
    const byAlias: Record<string, ClassModel> = {}
    models.forEach(model => { byAlias[model.alias] = model })
    const edges: ClassEdge[] = []
    models.forEach(model => {
        rowsOf(model)
            .filter(row => row.target && !row.target.mirror && byAlias[row.target.alias])
            .forEach(row => {
                const link = row.target!
                const other = byAlias[link.alias]
                const counterpart = link.counterpart ? rowOf(other, link.counterpart) : undefined
                const mirror = link.counterpart ? rowAnchor(other, link.counterpart) : undefined
                edges.push({
                    from: model.alias,
                    to: link.alias,
                    alias: row.alias,
                    sourceLabel: link.inverseMultiplicity,
                    targetLabel: row.multiplicity,
                    label: row.name,
                    bidirectional: link.bidirectional,
                    owner: ownerOf(row, counterpart),
                    sourceAnchor: rowAnchor(model, row.alias),
                    targetAnchor: mirror === undefined ? headerAnchor() : mirror
                })
            })
    })
    return edges
}

/**
 * O alias de LAYOUT de um domínio.
 *
 * Prefixado porque as caixas são indexadas por alias num espaço só, e domínio e coleção
 * têm aliases independentes: um domínio "situacao" e uma coleção "situacao" viveriam na
 * mesma chave, e as duas caixas viriam a ser uma. O prefixo tem `:`, que o slugify de
 * alias nunca produz, então não há alias de verdade que colida com um destes.
 */
const DOMAIN_PREFIX = 'domain:'

export function domainNode(alias: string): string {
    return DOMAIN_PREFIX + alias
}

/**
 * Uma caixa do diagrama: a coleção, ou o domínio de um atributo de seleção.
 *
 * A altura vem junto porque é ela que o layout consome, e quem sabe calculá-la é este
 * módulo — o classDiagram não conhece compartimento nenhum.
 */
export type DiagramBox =
    | { kind: 'class'; alias: string; height: number; model: ClassModel }
    | { kind: 'domain'; alias: string; height: number; domain: Domain }

/**
 * Os domínios que APARECEM no diagrama: só os referenciados por algum atributo.
 *
 * Domínio sem uso ficaria como caixa solta, sem ligação nenhuma, ocupando uma camada e
 * dizendo menos do que o card da seção de domínios já diz. A caixa existe porque uma
 * seta chega nela.
 *
 * A ordem é a do ambiente, e não a de descoberta: as caixas não podem trocar de lugar
 * conforme qual coleção referenciou o domínio primeiro.
 */
function usedDomains(models: ClassModel[], workspace: EnhancedWorkpace): Domain[] {
    const used = new Set<string>()
    models.forEach(model => rowsOf(model).forEach(row => {
        if (row.domain) used.add(row.domain.alias)
    }))
    return workspace.domains.filter(domain => used.has(domain.alias))
}

/**
 * As ligações de seleção: do atributo até a caixa do domínio.
 *
 * Sai da LINHA do atributo, como a de relacionamento, e chega no cabeçalho da caixa de
 * lá — a ligação é com o domínio inteiro, não com uma opção dele. Por isso também não
 * há rótulo na origem: quantos atributos usam o domínio é o número de setas que chegam,
 * e escrever uma multiplicidade ali seria inventar uma que não existe no modelo.
 *
 * Nunca é bidirecional nem tem dono: o domínio não sabe quem o usa, e nenhuma identidade
 * depende dele. Fica a seta simples, no sentido em que se lê.
 */
function domainEdges(models: ClassModel[], domains: Domain[]): ClassEdge[] {
    const known = new Set(domains.map(domain => domain.alias))
    const edges: ClassEdge[] = []
    models.forEach(model => {
        rowsOf(model)
            .filter(row => row.domain && known.has(row.domain.alias))
            .forEach(row => edges.push({
                from: model.alias,
                to: domainNode(row.domain!.alias),
                alias: row.alias,
                sourceLabel: '',
                targetLabel: row.multiplicity,
                label: row.name,
                bidirectional: false,
                owner: null,
                sourceAnchor: rowAnchor(model, row.alias),
                targetAnchor: headerAnchor()
            }))
    })
    return edges
}

/**
 * O diagrama em termos de modelo: as caixas e as ligações, numa passada.
 *
 * Os dois saem juntos porque dependem um do outro — a caixa de domínio só existe se uma
 * ligação chegar nela, e a ligação só existe se a caixa existir. Calculados em
 * chamadas separadas, um filtro mudado num lado deixaria o outro apontando para uma
 * caixa que não é desenhada, e o layout descartaria a aresta em silêncio.
 */
export function classDiagramModel(workspace: EnhancedWorkpace): { boxes: DiagramBox[]; edges: ClassEdge[] } {
    const models = classModels(workspace)
    const domains = usedDomains(models, workspace)
    return {
        boxes: [
            ...models.map(model => (
                {kind: 'class' as const, alias: model.alias, height: boxHeight(model), model}
            )),
            ...domains.map(domain => (
                {kind: 'domain' as const, alias: domainNode(domain.alias), height: domainHeight(domain), domain}
            ))
        ],
        edges: [...classEdges(models), ...domainEdges(models, domains)]
    }
}
